import { Link } from 'react-router-dom'
import { Shield, Heart, Users, ArrowRight, Sparkles, BadgeCheck } from 'lucide-react'
import Header from './Header'
import Footer from './Footer'
import { useOffline } from '../hooks/useOffline'
import { FAQ_ITEMS } from '../data/faq'
import FAQAccordion from './FAQAccordion'
import { useScrollReveal } from '../hooks/useScrollReveal.ts'
import WaitlistForm from './WaitlistForm'

export default function Landing() {
  useOffline()

  useScrollReveal()

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main id="main-content" className="flex-1 relative overflow-hidden" role="main" aria-label="Landing" tabIndex={-1}>
        <div className="pointer-events-none absolute top-0 left-0 w-96 h-96 rounded-full bg-primary-400/20 blur-3xl" style={{ animation: 'pulse 6s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
        <div className="container-max section-padding py-8 lg:py-12 space-y-14 lg:space-y-20 relative overflow-hidden">
          <div className="pointer-events-none absolute top-64 -right-24 w-72 h-72 rounded-full bg-secondary-400/20 blur-3xl animate-pulse" />

          <section className="mb-6 scroll-reveal" data-reveal>
            <div className="max-w-5xl mx-auto bg-white/70 dark:bg-black/30 backdrop-blur-sm rounded-3xl border border-neutral-dark/20 dark:border-white/20 p-6 sm:p-8 lg:p-12 hover-soft-fade text-center">
              <p className="inline-flex items-center gap-2 text-sm sm:text-base text-heading mb-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700 rounded-full px-4 py-2">
                <Sparkles className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                Private reflection · You-first onboarding
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl text-heading leading-tight font-normal fade-in">
                <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent font-bold">
                  SafePsy | Sage
                </span>
              </h1>
              <div className="mt-6 max-w-3xl mx-auto space-y-4 text-base sm:text-lg text-body leading-relaxed whitespace-normal break-words [text-wrap:pretty]">
                <p>
                  Your private AI companion for thought digestion.
                </p>
                <p>
                  Chat instantly with no sign-in, or use decentralized identity for fully encrypted conversations you control.
                </p>
                <p>
                  With decentralized identity, chats stay in your browser and encrypted history is stored as unreadable data your keys, your control. We never train on your data.
                </p>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/" className="btn-try-beta" aria-label="Try the SafePsy chat">
                  Try the chat
                  <ArrowRight className="w-5 h-5 shrink-0" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </section>

          <section className="scroll-reveal" data-reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl text-heading mb-8 text-center">
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">Why SafePsy is trusted for private thought digestion</span>
            </h2>
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-5">
              <div className="p-6 rounded-2xl border border-neutral-200 dark:border-white/20 bg-white/70 dark:bg-black/20 hover-soft-fade">
                <BadgeCheck className="w-7 h-7 text-secondary-600 dark:text-secondary-400 mb-3" />
                <p className="text-body text-sm sm:text-base"><strong className="text-heading">Privacy & security:</strong> Chat and summary data are encrypted at rest using AES-256-GCM (only ciphertext is stored). Data is transmitted over TLS 1.3. We do not log chat message content. Keys are derived from your wallet and not stored on SafePsy servers.</p>
              </div>
              <div className="p-6 rounded-2xl border border-neutral-200 dark:border-white/20 bg-white/70 dark:bg-black/20 hover-soft-fade">
                <Shield className="w-7 h-7 text-primary-600 dark:text-primary-400 mb-3" />
                <p className="text-body text-sm sm:text-base"><strong className="text-heading">Clinical boundaries:</strong> Sage is not a substitute for licensed therapy and does not provide diagnosis or medical treatment.</p>
              </div>
              <div className="p-6 rounded-2xl border border-neutral-200 dark:border-white/20 bg-white/70 dark:bg-black/20 hover-soft-fade">
                <Users className="w-7 h-7 text-accent-600 dark:text-accent-400 mb-3" />
                <p className="text-body text-sm sm:text-base"><strong className="text-heading">Compliance language:</strong> we implement measures aimed at GDPR compliance and support HIPAA-style safeguards (no HIPAA certification claim).</p>
              </div>
              <div className="p-6 rounded-2xl border border-neutral-200 dark:border-white/20 bg-white/70 dark:bg-black/20 hover-soft-fade">
                <Heart className="w-7 h-7 text-secondary-600 dark:text-secondary-400 mb-3" />
                <p className="text-body text-sm sm:text-base"><strong className="text-heading">Transparency:</strong> Decentralized Identity relies on blockchain; on-chain metadata is public, while chat content is off-chain and encrypted.</p>
              </div>
            </div>
                      </section>

          <section className="scroll-reveal" data-reveal>
            <WaitlistForm />
          </section>

          <section id="faq" className="scroll-reveal" data-reveal>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl text-heading mb-8 text-center">
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                Frequently asked questions
              </span>
            </h2>
            <FAQAccordion items={FAQ_ITEMS} answerClassName="text-xs sm:text-sm" />
          </section>
        </div>
      </main>
      <div className="fixed bottom-4 right-4 z-40">
        <Link
          to="/"
          className="btn-try-beta"
          aria-label="Try the SafePsy chat"
        >
          Try the chat
          <ArrowRight className="w-5 h-5 shrink-0" aria-hidden="true" />
        </Link>
      </div>
      <Footer />

      {/* FAQPage Structured Data for AI Search */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQ_ITEMS.map((item) => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.a
            }
          }))
        })
      }} />
    </div>
  )
}

